import { useMemo } from "react";
import { toast } from "react-toastify";

import _debounce from "lodash/debounce";

import ColorPicker from "components/base/ColorPicker";
import { useMapContext } from "components/map/hooks/useMapContext";
import { updateShapefileLayerStyles } from "components/map/utils/updateShapefileLayerStyles";
import { useProjectContext } from "components/project/projects/context";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";
import { useUpdateProjectShapefile } from "components/project/shapefiles/hooks";
import {
  INVALID_FILL_PATTERN_MESSAGE,
  INVALID_LINE_STYLE_MESSAGE,
  ShapefileStyleProp,
  type ShapefileStylePropT,
  isFillPattern,
  isLineStyle
} from "components/project/shapefiles/shapefiles.constants";

interface IProjectShapefileColorPickerProps {
  shapefileId: string;
  color: string;
  strokeColor: string;
  thickness: number;
  lineStyle?: string;
  fillPattern?: string;
  opacity: number;
  fileType?: string;
}

export function ProjectShapefileColorPicker({
  shapefileId,
  color,
  strokeColor,
  thickness,
  lineStyle,
  fillPattern,
  opacity,
  fileType
}: IProjectShapefileColorPickerProps) {
  const { selectedProjectId } = useProjectContext();
  const { mapbox } = useMapContext();

  const projectPermissions = useSelectedProjectPermissions();

  const { updateProjectShapefile } = useUpdateProjectShapefile();

  const debouncedUpdateProjectShapefileOpacity = useMemo(
    () =>
      _debounce((key: ShapefileStylePropT, value: number, shapefileId: string) => {
        if (mapbox) {
          updateShapefileLayerStyles({
            mapbox,
            shapefileId,
            property: key,
            value
          });
        }
        updateProjectShapefile.mutate({
          body: {
            key,
            value
          },
          projectId: selectedProjectId,
          shapefileId: shapefileId
        });
      }, 500),
    [selectedProjectId, mapbox]
  );

  const handleChange = (key: ShapefileStylePropT, value: string) => {
    if (key === ShapefileStyleProp.LineStyle && !isLineStyle(value)) {
      toast.error(INVALID_LINE_STYLE_MESSAGE);
      return;
    }

    if (key === ShapefileStyleProp.FillPattern && !isFillPattern(value)) {
      toast.error(INVALID_FILL_PATTERN_MESSAGE);
      return;
    }

    // Only update layer styles directly for shapetiles mode
    // GeoJSON mode updates via source data refresh
    if (mapbox) {
      updateShapefileLayerStyles({
        mapbox,
        shapefileId,
        property: key,
        value
      });
    }
    updateProjectShapefile.mutate({
      body: {
        key,
        value
      },
      projectId: selectedProjectId,
      shapefileId: shapefileId
    });
  };

  return (
    <ColorPicker
      onChange={handleChange}
      onChangeOpacity={(key, value) =>
        debouncedUpdateProjectShapefileOpacity(key, value, shapefileId)
      }
      onChangeThickness={(key, value) =>
        debouncedUpdateProjectShapefileOpacity(key, value, shapefileId)
      }
      onChangeLineStyle={(key, value) => handleChange(key, value)}
      onChangeFillPattern={(key, value) => handleChange(key, value)}
      values={{
        color: color,
        strokeColor: strokeColor,
        thickness: thickness,
        ...(fillPattern !== undefined && { fillPattern: fillPattern ?? "none" }),
        ...(lineStyle !== undefined && { lineStyle: lineStyle ?? "solid" }),
        opacity: (opacity ?? 1) * 100
      }}
      isReadOnly={!projectPermissions.canEditShapefiles}
      isAtShapefileLevel={true}
      isGeoTIFF={fileType === "GeoTiff"}
    />
  );
}
